// src/odoo/transactions_dashboard/components/RankingBarChart.tsx
// All comments are in English.

import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export interface RankingBarRow {
  id: number | string;
  label: string;
  value: number;
  brandGrouping?: string;
  secondaryLabel?: string;
  secondaryValue?: number;
}

interface Props {
  title: string;
  description?: string;
  showHeader?: boolean;
  rows: RankingBarRow[];
  valueLabel: string;
  valueFormatter: (value: number) => string;
  secondaryValueFormatter?: (value: number) => string;
  barColor: string;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
  onBarClick?: (row: RankingBarRow) => void;
}

const RankingBarChart: React.FC<Props> = ({
  title,
  description,
  showHeader = true,
  rows,
  valueLabel,
  valueFormatter,
  secondaryValueFormatter,
  barColor,
  isLoading,
  error,
  emptyMessage = "No data for this period.",
  onBarClick,
}) => {
  if (isLoading) {
    return (
      <Card className="border-slate-200/70 shadow-sm">
        {showHeader ? (
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </CardHeader>
        ) : null}
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-rose-200/70 shadow-sm">
        {showHeader ? (
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </CardHeader>
        ) : null}
        <CardContent>
          <p className="text-sm text-rose-700">Failed to load data: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return (
      <Card className="border-slate-200/70 shadow-sm">
        {showHeader ? (
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </CardHeader>
        ) : null}
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const tooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload as RankingBarRow | undefined;
    if (!row) return null;

    return (
      <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="text-sm font-semibold text-slate-900">{row.label}</div>
        {row.brandGrouping ? (
          <div className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
            {row.brandGrouping}
          </div>
        ) : null}
        <div className="mt-2 space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-slate-500">{valueLabel}</span>
            <span className="font-medium text-slate-900">{valueFormatter(row.value)}</span>
          </div>
          {row.secondaryLabel && row.secondaryValue !== undefined ? (
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{row.secondaryLabel}</span>
              <span className="font-medium text-slate-900">
                {secondaryValueFormatter
                  ? secondaryValueFormatter(row.secondaryValue)
                  : String(row.secondaryValue)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-slate-200/70 shadow-sm">
      {showHeader ? (
        <CardHeader className="pb-3">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
      ) : null}
      <CardContent>
        <div className="h-[360px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: 12 }}
                tickFormatter={valueFormatter}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={180}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#0f172a", fontSize: 12 }}
              />
              <Tooltip content={tooltip} cursor={{ fill: "rgba(15, 23, 42, 0.04)" }} />
              <Bar
                dataKey="value"
                fill={barColor}
                radius={[0, 10, 10, 0]}
                barSize={18}
                onClick={
                  onBarClick
                    ? (data: unknown) => {
                        const row = (data as { payload?: RankingBarRow })?.payload;
                        if (row) {
                          onBarClick(row);
                        }
                      }
                    : undefined
                }
                style={onBarClick ? { cursor: "pointer" } : undefined}
              >
                <LabelList
                  dataKey="value"
                  position="right"
                  fill="#0f172a"
                  fontSize={11}
                  formatter={valueFormatter}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default RankingBarChart;
